/**
 * GA4 / Google Ads cross-domain attribution helpers (KAIROS-169, Capa 2).
 *
 * The minisite lives on `*.site.agendapro.com` while the booking flow crosses to
 * other `agendapro.com` subdomains (legacy domain, `/propay/pay` checkout) and, for
 * some clients, to their own external domain. Without a GA4 `linker` covering those
 * domains the `_gl`/`gclid` parameters are dropped and Google Ads can't attribute the
 * booking. These helpers build a robust linker and forward the attribution params on
 * the programmatic redirects GA's auto-linker does not decorate.
 */

const GA4_ID_REGEX = /^G-[A-Z0-9]+$/i;
const DOMAIN_REGEX = /^[a-z0-9.-]+\.[a-z]{2,}$/i;
const META_PIXEL_ID_REGEX = /^\d{8,20}$/;

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
// Google Ads click identifiers + GA linker param. `_gl` carries the GA client id across
// domains; gclid/gbraid/wbraid are the Ads click ids consumed by the checkout downstream.
const ADS_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid', '_gl'];

/** A measurement ID is only usable if it is a real GA4 id (`G-XXXX`). */
export const isValidGa4Id = (id?: string): boolean => !!id && GA4_ID_REGEX.test(id.trim());

/** Returns the trimmed GA4 id, or undefined when the stored value is junk (UA-/GTM-/AW-/HTML/empty). */
export const sanitizeGa4Id = (id?: string): string | undefined => {
  const trimmed = id?.trim();

  return trimmed && GA4_ID_REGEX.test(trimmed) ? trimmed : undefined;
};

const sanitizeDomain = (domain?: string): string | undefined => {
  const trimmed = domain?.trim();

  return trimmed && DOMAIN_REGEX.test(trimmed) ? trimmed : undefined;
};

/**
 * Domains for a GA4 `linker`. Always includes the agendapro root domain so the
 * `legacy.agendapro.com → *.site.agendapro.com → /propay/pay` hops preserve the GA
 * client id, plus the client's own external domain when configured.
 */
export const buildLinkerDomains = (cookieDomain?: string, customTrackerDomain?: string): string[] => {
  const domains = [sanitizeDomain(cookieDomain), sanitizeDomain(customTrackerDomain)].filter(
    (domain): domain is string => Boolean(domain),
  );

  return Array.from(new Set(domains));
};

const linkerConfigFragment = (domains: string[]): string =>
  domains.length ? `cookie_flags: 'SameSite=None;Secure', linker: { domains: ${JSON.stringify(domains)} },` : '';

/**
 * Builds the inline `gtag` init script. Both the AgendaPro global tracker and the
 * client tracker get a linker covering the agendapro domains; the client tracker also
 * covers its external domain. The client id is sanitized so junk values (HTML, UA-,
 * GTM-, whitespace) are never interpolated into the inline script.
 */
export const buildGtagInitScript = ({
  gaTrackingId,
  customTracker,
  customTrackerDomain,
  cookieDomain,
}: {
  gaTrackingId?: string;
  customTracker?: string;
  customTrackerDomain?: string;
  cookieDomain?: string;
}): string => {
  const safeCustomTracker = sanitizeGa4Id(customTracker);
  const globalLinker = linkerConfigFragment(buildLinkerDomains(cookieDomain));
  const customLinker = linkerConfigFragment(buildLinkerDomains(cookieDomain, customTrackerDomain));

  const customTrackerConfig = safeCustomTracker
    ? `gtag('config', '${safeCustomTracker}', {
        ${customLinker}
        page_path: window.location.pathname,
      });`
    : '';

  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaTrackingId}', {
      ${globalLinker}
      page_path: window.location.pathname,
    });
    ${customTrackerConfig}
  `;
};

/** Pixel id goes quoted: as a JS number, ids above 9007199254740991 get rounded and the pixel never registers. */
export const buildMetaPixelInit = (pixelId?: string): string => {
  const trimmed = pixelId?.trim();

  return trimmed && META_PIXEL_ID_REGEX.test(trimmed) ? `fbq('init', '${trimmed}');` : '';
};

/**
 * Extracts the attribution params (utm_* + Ads click ids) from a query string and
 * returns them joined as `a=1&b=2` (no leading `&`). Used to forward attribution on
 * the programmatic `window.location.href` redirect to the checkout, which GA4's
 * auto-linker does not decorate.
 */
export const getForwardedAttributionParams = (search?: string): string => {
  const source = new URLSearchParams(search ?? '');
  const forwarded = new URLSearchParams();

  [...UTM_PARAMS, ...ADS_CLICK_PARAMS].forEach((key) => {
    const value = source.get(key);

    if (value) {
      forwarded.set(key, value);
    }
  });

  return forwarded.toString();
};
